import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { useEditor } from './state/store';
import './styles.css';

/*
  Sentry init — DEVE ser antes do createRoot. O SDK precisa estar
  ativo quando a árvore React monta pra capturar erros em componentes,
  effects, etc. Em produção também ativa source maps via vite-plugin
  (futuro — não setado ainda).

  Defensive: try/catch silencioso. Se o init falhar (módulo ausente,
  DSN bloqueado por adblocker, CORS), o editor continua funcionando
  normal. Observability não é crítico pra UX.
*/
try {
  Sentry.init({
    dsn: 'https://a5a2d44c278f7bb4541d90a14b5f24ec@sentry.lunacheckout.com/6',
    environment: import.meta.env.MODE,
    /*
      sendDefaultPii: true coleta IP, headers de browser, geolocation
      aproximado. Útil pra reproduzir bugs específicos de
      ambiente/região. Self-hosted Sentry da Luna — dados não saem
      pra fora.
    */
    sendDefaultPii: true,
    /*
      Performance monitoring DESATIVADO por enquanto (sample rate 0).
      Subir gradual via env var quando precisarmos profiling de
      renders pesados. 0.1 captura 10% das transactions.
    */
    tracesSampleRate: 0,
    /*
      Filtra erros conhecidos do iframe srcdoc (preview do tema) —
      Sentry pega scripts injetados dentro do iframe via integration
      default, mas erros do TEMA (que tem seu próprio Sentry no projeto
      4) não devem poluir o do EDITOR (projeto 5).

      ResizeObserver loop errors também são ignorados — browser quirk
      benigno que sempre aparece e não indica bug real.
    */
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event, hint) {
      const err = hint?.originalException as any;
      const msg = String(err?.message || err?.name || '').toLowerCase();
      if (msg.includes('abort') || msg.includes('cancelled') || msg.includes('canceled')) {
        return null;
      }
      return event;
    },
  });
} catch (err) {
  // eslint-disable-next-line no-console
  console.warn('[Sentry] init failed (non-fatal):', err);
}

// Dev-only: expose the store on window so we can poke it from the console.
if (import.meta.env.DEV) {
  (window as any).__LC = { useEditor };
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/*
      Sentry.ErrorBoundary captura erros que React não consegue lidar
      (crashes em render/effect que escapariam pro window.onerror).
      `fallback` mostra UI mínima ao invés da tela branca. Em produção
      o erro vai pro Sentry; em dev o React DevTools overlay aparece
      por cima (não interfere).
    */}
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', maxWidth: 540, margin: '40px auto' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Algo deu errado</h2>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
            Reportamos o erro automaticamente. Você pode tentar de novo:
          </p>
          <pre style={{
            fontSize: 12,
            background: '#f5f5f5',
            padding: 12,
            borderRadius: 6,
            overflowX: 'auto',
            color: '#b91c1c',
          }}>
            {String((error as Error)?.message || error)}
          </pre>
          <button
            onClick={resetError}
            style={{
              marginTop: 16,
              padding: '8px 16px',
              background: '#111',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}
      showDialog={false}
    >
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
